import { useCallback, useEffect, useState } from 'react'
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  PasswordInput,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core'
import {
  IconAlertCircle,
  IconLock,
  IconPencil,
  IconRefresh,
  IconRestore,
} from '@tabler/icons-react'
import { useArchbaseRemoteServiceApi } from '@archbase/data'
import { API_TYPE } from '@ioc/IOCTypes'
import type { DoclenzSettingsService } from '@services/DoclenzSettingsService'
import {
  ORIGIN_COLOR,
  ORIGIN_LABEL,
  SETTING_HELP,
  type ResolvedSettingDto,
} from '@domain/settings/SettingsDto'

/** Parâmetros cujo valor nunca volta do servidor em claro. */
const SECRET_KEYS = new Set(['llm.api-key'])

export function SettingsView() {
  const settingsApi = useArchbaseRemoteServiceApi<DoclenzSettingsService>(API_TYPE.DoclenzSettings)
  const [settings, setSettings] = useState<ResolvedSettingDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<ResolvedSettingDto | null>(null)
  const [draft, setDraft] = useState('')
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await settingsApi.findAll()
      setSettings(response.settings || [])
    } catch (err: any) {
      setError(err?.message || 'Não foi possível carregar as configurações.')
    } finally {
      setLoading(false)
    }
  }, [settingsApi])

  useEffect(() => {
    load()
  }, [load])

  const openEditor = (setting: ResolvedSettingDto) => {
    setEditing(setting)
    // Segredo nunca é pré-preenchido: o que veio do servidor é a máscara, e mandá-la de volta
    // gravaria "sk-or-v1****3f2a" como se fosse a chave.
    setDraft(SECRET_KEYS.has(setting.key) ? '' : setting.value)
    setSaveError(null)
  }

  const save = async () => {
    if (!editing) return
    setSaving(true)
    setSaveError(null)
    try {
      await settingsApi.update(editing.key, draft)
      setEditing(null)
      await load()
    } catch (err: any) {
      setSaveError(messageFor(err))
    } finally {
      setSaving(false)
    }
  }

  const reset = async (setting: ResolvedSettingDto) => {
    setError(null)
    try {
      await settingsApi.reset(setting.key)
      await load()
    } catch (err: any) {
      setError(messageFor(err))
    }
  }

  return (
    /*
      O contêiner de abas do Archbase tem altura definida e não rola: sem isto a lista cresce
      além do fundo da tela e as últimas linhas simplesmente somem, sem barra nenhuma. A rolagem
      tem de ser da view, não da página.

      minHeight 0 não é decoração: num filho de flex o padrão é `auto`, que impede o encolhimento
      e faz o overflow vazar em vez de rolar.
    */
    <Stack
      p="md"
      gap="lg"
      style={{ height: '100%', minHeight: 0, overflowY: 'auto' }}
    >
      <Group justify="space-between">
        <Title order={3}>Configurações</Title>
        <Button variant="default" leftSection={<IconRefresh size={16} />} onClick={load}>
          Atualizar
        </Button>
      </Group>

      <Alert color="blue" variant="light">
        Alterações valem na próxima requisição, sem reiniciar a API. Parâmetros com{' '}
        <IconLock size={13} style={{ verticalAlign: 'text-bottom' }} /> foram travados pelo
        ambiente: aparecem aqui para você saber o que está valendo, mas só mudam por deploy.
      </Alert>

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {error}
        </Alert>
      )}

      {loading ? (
        <Group justify="center" p="xl">
          <Loader />
        </Group>
      ) : (
        <Card withBorder padding="sm">
          {/*
            A tabela tem coluna de texto de ajuda, que fica larga em tela pequena. Sem o
            ScrollContainer ela empurra o layout para fora da viewport em vez de rolar dentro do
            proprio card — o conteudo some pela direita sem barra nenhuma.
          */}
          <Table.ScrollContainer minWidth={760}>
          <Table striped highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Parâmetro</Table.Th>
                <Table.Th>Valor em vigor</Table.Th>
                <Table.Th w={130}>Origem</Table.Th>
                <Table.Th w={110}>Ações</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {settings.map((setting) => (
                <Table.Tr key={setting.key}>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      {setting.locked && (
                        <Tooltip label="Travado pelo ambiente (devops)" withArrow>
                          <IconLock size={14} />
                        </Tooltip>
                      )}
                      <Text fw={500} size="sm" ff="monospace">
                        {setting.key}
                      </Text>
                    </Group>
                    {SETTING_HELP[setting.key] && (
                      <Text size="xs" c="dimmed" mt={2}>
                        {SETTING_HELP[setting.key]}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" ff="monospace" style={{ wordBreak: 'break-word' }}>
                      {setting.value || <Text component="span" c="dimmed">(vazio)</Text>}
                    </Text>
                    {setting.origin === 'PLATFORM' && (
                      <Text size="xs" c="dimmed">
                        ambiente: {setting.environment_value || '(vazio)'}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Badge color={ORIGIN_COLOR[setting.origin]} variant="light" size="sm">
                      {ORIGIN_LABEL[setting.origin]}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      <Tooltip
                        label={setting.locked ? 'Travado pelo ambiente' : 'Alterar'}
                        withArrow
                      >
                        <ActionIcon
                          variant="subtle"
                          disabled={setting.locked}
                          onClick={() => openEditor(setting)}
                        >
                          <IconPencil size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip
                        label={
                          setting.origin === 'PLATFORM'
                            ? `Voltar para o valor do ambiente (${setting.environment_value || 'vazio'})`
                            : 'Nada a reverter — já está no valor do ambiente'
                        }
                        withArrow
                      >
                        <ActionIcon
                          variant="subtle"
                          color="orange"
                          disabled={setting.locked || setting.origin !== 'PLATFORM'}
                          onClick={() => reset(setting)}
                        >
                          <IconRestore size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          </Table.ScrollContainer>
        </Card>
      )}

      <Modal
        opened={editing !== null}
        onClose={() => setEditing(null)}
        title={editing ? `Alterar ${editing.key}` : ''}
      >
        {editing && (
          <Stack gap="md">
            {SETTING_HELP[editing.key] && (
              <Text size="sm" c="dimmed">
                {SETTING_HELP[editing.key]}
              </Text>
            )}

            {saveError && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
                {saveError}
              </Alert>
            )}

            {editing.allowed_values.length > 0 ? (
              <Select
                label="Valor"
                data={editing.allowed_values}
                value={draft}
                onChange={(value) => setDraft(value || '')}
                allowDeselect={false}
              />
            ) : SECRET_KEYS.has(editing.key) ? (
              <PasswordInput
                label="Valor"
                description="Digite a chave inteira. Depois de gravada não há como lê-la de volta — só substituir."
                placeholder="sk-..."
                value={draft}
                onChange={(e) => setDraft(e.currentTarget.value)}
              />
            ) : (
              <TextInput
                label="Valor"
                value={draft}
                onChange={(e) => setDraft(e.currentTarget.value)}
              />
            )}

            <Text size="xs" c="dimmed">
              Valor do ambiente: {editing.environment_value || '(vazio)'} — é para onde um reset
              volta.
            </Text>

            <Group justify="flex-end">
              <Button variant="default" onClick={() => setEditing(null)}>
                Cancelar
              </Button>
              <Button onClick={save} loading={saving} disabled={!draft.trim()}>
                Gravar
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  )
}

/** Traduz as respostas que o servidor distingue, em vez de mostrar "Request failed". */
function messageFor(err: any): string {
  const status = err?.response?.status ?? err?.status
  const detail = err?.response?.data?.error ?? err?.message
  if (status === 409) {
    return detail || 'Este parâmetro está travado pelo ambiente e só muda por deploy.'
  }
  if (status === 503) {
    return (
      detail ||
      'Falta a chave de cifra (DOCLENZ_SECRETS_KEY) no servidor. Segredos não são gravados sem ' +
        'ela — de propósito, para não ficarem em texto puro.'
    )
  }
  if (status === 400) {
    return detail || 'Valor inválido para este parâmetro.'
  }
  return detail || 'Não foi possível gravar.'
}
